import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  type DbDraft,
  type DbDraftType,
  type DraftListFilters,
  useDeleteDraftMutation,
  useDraftsQuery,
} from "hooks/queries/useDraftsQuery";
import {
  listLocalDrafts,
  removeLocalDraft,
  type LocalDraftSummary,
} from "util/localDrafts";
import { useUser } from "util/UserContext";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";

type DraftItem = DbDraft | LocalDraftSummary;

interface DraftsListProps {
  filters?: DraftListFilters;
  includeLocal?: boolean;
  compact?: boolean;
  heading?: string;
  emptyState?: React.ReactNode;
  onOpenDraft?: (draft: DraftItem) => void;
}

function isLocalDraft(draft: DraftItem): draft is LocalDraftSummary {
  return draft.source === "local";
}

function getDraftTimestamp(draft: DraftItem): string | null {
  return draft.updated_at || draft.created_at || null;
}

function getDraftTitle(draft: DraftItem): string {
  if (isLocalDraft(draft)) {
    return draft.title;
  }
  if (draft.title && draft.title.trim().length > 0 && draft.title !== "[no_title]") {
    return draft.title;
  }
  const content = (draft.content || draft.body || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (content) {
    return content.slice(0, 80);
  }
  const kind = draft.kind || draft.entityType || "draft";
  return `${kind.charAt(0).toUpperCase()}${kind.slice(1)} draft`;
}

function getDraftSubtitle(draft: DraftItem, localDescription: string): string {
  if (isLocalDraft(draft)) {
    return draft.description || localDescription;
  }
  const parts = [draft.kind, draft.draftType === "post" ? "post" : "comment"].filter(Boolean);
  return parts.join(" · ");
}

function filterLocalDrafts(drafts: LocalDraftSummary[], filters: DraftListFilters): LocalDraftSummary[] {
  return drafts.filter((draft) => {
    if (filters.source && draft.draftType !== filters.source) {
      return false;
    }
    if (filters.kind && draft.kind !== filters.kind && draft.entityType !== filters.kind) {
      return false;
    }
    if (filters.collectionType) {
      const collectionType = draft.value.collectionType;
      return draft.entityType === "collection" && collectionType === filters.collectionType;
    }
    return true;
  });
}

export default function DraftsList({
  filters = {},
  includeLocal = true,
  compact = false,
  heading,
  emptyState,
  onOpenDraft,
}: DraftsListProps) {
  const ui = getDraftUiStrings(resolveDraftLocale());
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const deleteDraftMutation = useDeleteDraftMutation();
  const [offset, setOffset] = useState(0);
  const [dbDrafts, setDbDrafts] = useState<DbDraft[]>([]);
  const [localVersion, setLocalVersion] = useState(0);
  const limit = filters.limit ?? 20;
  const queryFilters = useMemo(
    () => ({ ...filters, limit, offset }),
    [filters, limit, offset]
  );
  const draftsQuery = useDraftsQuery(queryFilters, Boolean(currentUser));

  useEffect(() => {
    setOffset(0);
    setDbDrafts([]);
  }, [filters.source, filters.kind, filters.collectionType]);

  useEffect(() => {
    const items = draftsQuery.data?.items;
    if (!items) {
      return;
    }
    setDbDrafts((current) => (offset === 0 ? items : [...current, ...items]));
  }, [draftsQuery.data?.items, offset]);

  const localDrafts = useMemo(() => {
    void localVersion;
    if (!includeLocal) {
      return [];
    }
    return filterLocalDrafts(listLocalDrafts(currentUser?.id), filters);
  }, [currentUser?.id, filters, includeLocal, localVersion]);

  const combinedDrafts = useMemo(() => {
    const merged: DraftItem[] = offset === 0 ? [...localDrafts, ...dbDrafts] : [...localDrafts, ...dbDrafts];
    return merged.sort((left, right) => {
      const leftTs = new Date(getDraftTimestamp(left) || 0).getTime();
      const rightTs = new Date(getDraftTimestamp(right) || 0).getTime();
      return rightTs - leftTs;
    });
  }, [dbDrafts, localDrafts, offset]);

  const totalDb = draftsQuery.data?.total ?? dbDrafts.length;
  const hasMoreDb = dbDrafts.length < totalDb;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (draftsQuery.isFetching || !hasMoreDb) {
        return;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) {
          setOffset((current) => current + limit);
        }
      });
      if (node) {
        observerRef.current.observe(node);
      }
    },
    [draftsQuery.isFetching, hasMoreDb, limit]
  );

  const handleOpenDraft = (draft: DraftItem) => {
    if (onOpenDraft) {
      onOpenDraft(draft);
      return;
    }
    if (draft.draftType === "comment") {
      navigate(`/comment/${draft.id}`);
      return;
    }
    navigate(`/${draft.entityType}/${draft.id}`);
  };

  const handleDeleteDraft = async (draft: DraftItem) => {
    try {
      if (isLocalDraft(draft)) {
        removeLocalDraft(draft.logicalKey);
        setLocalVersion((current) => current + 1);
        return;
      }
      await deleteDraftMutation.mutateAsync({
        draftType: draft.draftType as DbDraftType,
        entityType: draft.entityType,
        id: draft.id,
      });
      setDbDrafts((current) => current.filter((item) => item.id !== draft.id || item.draftType !== draft.draftType));
    } catch {
      toast.error(ui.draftsError);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <section className={compact ? "space-y-2" : "space-y-4"}>
      {heading ? (
        <h2 className={compact ? "text-sm font-semibold" : "text-lg font-semibold"}>{heading}</h2>
      ) : null}
      {draftsQuery.isLoading && combinedDrafts.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>{ui.draftsLoading}</span>
        </div>
      ) : draftsQuery.isError ? (
        <p className="text-sm text-warning-red dark:text-warning-red-dark">{ui.draftsError}</p>
      ) : combinedDrafts.length === 0 ? (
        emptyState || <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.draftsEmpty}</p>
      ) : (
        <div className="space-y-2">
          {combinedDrafts.map((draft, index) => {
            const key = isLocalDraft(draft)
              ? draft.logicalKey
              : `${draft.draftType}:${draft.id}`;
            const isLast = index === combinedDrafts.length - 1;
            return (
              <div
                key={key}
                ref={isLast ? lastItemRef : undefined}
                className="flex items-center justify-between gap-3 rounded-xl border border-eoe-border-main bg-eoe-bg-light p-3 text-left dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left focus:outline-none focus:ring-2 focus:ring-eoe-fg"
                  onClick={() => handleOpenDraft(draft)}
                  aria-label={ui.openDraft}
                >
                  <span className="block truncate text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {getDraftTitle(draft)}
                  </span>
                  <span className="mt-1 block truncate text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {getDraftSubtitle(draft, ui.localDraftDescription)}
                  </span>
                </button>
                <span className="rounded-full bg-eoe-bg px-2 py-1 text-[11px] font-semibold text-eoe-text-sub dark:bg-eoe-dark-bg-light dark:text-eoe-dark-text-sub">
                  {isLocalDraft(draft) ? ui.localDraftBadge : ui.dbDraftBadge}
                </span>
                <button
                  type="button"
                  className="rounded-full p-2 text-eoe-text-sub hover:bg-eoe-bg dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-bg-light"
                  onClick={() => void handleDeleteDraft(draft)}
                  aria-label={ui.deleteDraft}
                >
                  <Trash2 className="size-4" aria-hidden />
                </button>
              </div>
            );
          })}
          {draftsQuery.isFetching && combinedDrafts.length > 0 ? (
            <div className="flex items-center justify-center py-2 text-eoe-text-sub dark:text-eoe-dark-text-sub">
              <Loader2 className="size-4 animate-spin" aria-hidden />
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
